import { useEffect, useState } from "react";
import { Scale, Gavel, Trash2, Building2, User2, Users, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SUB_ACCOUNTS, formatTRY, mahkemeTuruLabel } from "@/lib/constants";
import SubAccountPanel from "@/components/SubAccountPanel";
import { listDocuments } from "@/lib/api";

export default function CaseDetail({ caseData, onRefresh, onDelete }) {
  const [activeSub, setActiveSub] = useState("dava_acilis");
  const [docCounts, setDocCounts] = useState({});

  const loadCounts = async () => {
    try {
      const docs = await listDocuments(caseData.id);
      const counts = {};
      for (const d of docs) {
        counts[d.sub_account] = (counts[d.sub_account] || 0) + 1;
      }
      // merge sonuc_lehe/aleyhe into sonuc
      counts.sonuc = (counts.sonuc_lehe || 0) + (counts.sonuc_aleyhe || 0);
      setDocCounts(counts);
    } catch {}
  };

  useEffect(() => { loadCounts(); /* eslint-disable-next-line */ }, [caseData.id]);

  const refreshAll = () => { loadCounts(); onRefresh?.(); };

  const resultBadge = () => {
    if (caseData.result === "basarili")
      return <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white">BAŞARILI (+)</Badge>;
    if (caseData.result === "basarisiz")
      return <Badge className="bg-red-600 hover:bg-red-600 text-white">BAŞARISIZ (-)</Badge>;
    return <Badge className="bg-slate-700 hover:bg-slate-700 text-slate-200">DERDEST</Badge>;
  };

  return (
    <div className="p-6 space-y-5" data-testid="case-detail">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 border-b border-slate-800 pb-5">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <Scale className="w-6 h-6 text-amber-500" />
            <h1 className="font-serif text-3xl font-bold text-slate-100">{caseData.dava_no}</h1>
            {resultBadge()}
          </div>
          <div className="mt-2 text-slate-300 flex items-center gap-2 flex-wrap">
            <Building2 className="w-4 h-4 text-slate-500" />
            <span>{caseData.mahkeme_adi}</span>
            <Badge variant="outline" className="border-amber-700/40 text-amber-500 text-[10px] uppercase tracking-wider">
              {mahkemeTuruLabel(caseData.mahkeme_turu)}
            </Badge>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
            <div className="flex items-center gap-2 text-slate-400"><User2 className="w-4 h-4" /> Müvekkil: <span className="text-slate-200">{caseData.muvekkil || "—"}</span></div>
            <div className="flex items-center gap-2 text-slate-400"><Users className="w-4 h-4" /> Karşı Taraf: <span className="text-slate-200">{caseData.karsi_taraf || "—"}</span></div>
          </div>
          {caseData.konu && <div className="mt-2 text-sm text-slate-400 italic">"{caseData.konu}"</div>}
        </div>
        <div className="text-right space-y-1 shrink-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">İş Bedeli</div>
          <div className="text-2xl font-serif text-amber-500">{formatTRY(caseData.is_bedeli)}</div>
          <div className="text-xs text-slate-400">Tahsil: {formatTRY(caseData.total_paid)}</div>
          <div className="text-xs text-red-400">Kalan: {formatTRY(caseData.balance)}</div>
          <Button
            variant="ghost" size="sm" onClick={onDelete}
            className="text-red-400 hover:text-red-300 hover:bg-red-950/40 mt-2"
            data-testid="delete-case-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Dava Sil
          </Button>
        </div>
      </div>

      {/* Sub-account tabs */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">Alt Hesaplar</div>
        <div className="flex flex-wrap gap-2">
          {SUB_ACCOUNTS.map((sa) => (
            <button
              key={sa.key}
              onClick={() => setActiveSub(sa.key)}
              className={`sub-tab-btn ${activeSub === sa.key ? "active" : ""}`}
              data-testid={`sub-tab-${sa.key}`}
            >
              <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">
                {sa.key === "sonuc" ? <Gavel className="w-3 h-3 inline mr-1 text-amber-500" /> : <FileText className="w-3 h-3 inline mr-1 text-slate-500" />}
                {sa.short}
              </span>
              <span className="text-sm font-semibold text-slate-100 mt-0.5">{sa.label}</span>
              <span className="text-[10px] text-slate-500 mt-0.5">{docCounts[sa.key] || 0} belge</span>
            </button>
          ))}
        </div>
      </div>

      <Separator className="bg-slate-800" />

      {/* Active panel */}
      <SubAccountPanel caseData={caseData} subKey={activeSub} onRefresh={refreshAll} />
    </div>
  );
}
